(() => {
  if (window.VistoMotion) return

  const instances = new WeakMap()

  const getMotionNodes = (root = document) => {
    const nodes = []

    if (root instanceof HTMLElement && root.matches('[data-visto-motion]')) {
      nodes.push(root)
    }

    if ('querySelectorAll' in root) {
      nodes.push(...root.querySelectorAll('[data-visto-motion]'))
    }

    return nodes
  }

  const parseOptions = (node) => {
    const source = node.querySelector('[data-visto-motion-options]')
    const raw = source?.textContent || node.dataset.vistoMotionOptions || '{}'

    try {
      return JSON.parse(raw)
    } catch (error) {
      console.warn('[VistoMotion] Invalid motion options.', error)
      return {}
    }
  }

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const initNode = async (node) => {
    if (instances.has(node)) return instances.get(node)

    node.dataset.vistoMotionState = 'loading'

    const motion = await window.VistoVendor.loadFramerMotionDom()
    const options = parseOptions(node)
    const animate = motion.animate || motion.default?.animate

    if (typeof animate !== 'function') {
      node.dataset.vistoMotionState = 'unavailable'
      return null
    }

    const targetSelector = node.dataset.vistoMotionTarget
    const targets = targetSelector
      ? Array.from(node.querySelectorAll(targetSelector))
      : [node]
    const from = options.from || { opacity: 0, y: 16 }
    const to = options.to || { opacity: 1, y: 0 }
    const transition = {
      duration: Number(options.duration || 0.48),
      delay: Number(options.delay || 0),
      easing: options.easing || 'ease-out',
    }

    const run = () => {
      if (prefersReducedMotion()) {
        targets.forEach((target) => {
          if (target instanceof HTMLElement) {
            target.style.opacity = ''
            target.style.transform = ''
          }
        })
        return null
      }

      return animate(targets, [from, to], transition)
    }

    let observer = null
    let controls = null

    if (node.dataset.vistoMotionTrigger === 'visible' && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return

          controls = run()
          observer?.disconnect()
          observer = null
        })
      }, { threshold: Number(options.threshold || 0.18) })
      observer.observe(node)
    } else {
      controls = run()
    }

    const instance = {
      destroy() {
        observer?.disconnect()
        controls?.stop?.()
        instances.delete(node)
        delete node.dataset.vistoMotionState
      },
    }

    instances.set(node, instance)
    node.dataset.vistoMotionState = 'mounted'
    return instance
  }

  const destroyNode = (node) => {
    instances.get(node)?.destroy()
  }

  const init = (root = document) => {
    if (!window.VistoVendor) return Promise.resolve([])

    const nodes = getMotionNodes(root)
    if (!nodes.length) return Promise.resolve([])

    return Promise.all(nodes.map((node) => initNode(node).catch((error) => {
      node.dataset.vistoMotionState = 'error'
      console.error('[VistoMotion] Failed to initialize motion.', error)
      return null
    })))
  }

  const destroy = (root = document) => {
    getMotionNodes(root).forEach(destroyNode)
  }

  window.VistoMotion = {
    init,
    destroy,
    initNode,
    destroyNode,
  }

  document.addEventListener('DOMContentLoaded', () => init())
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) init(event.target)
  })
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) destroy(event.target)
  })
})()
